import type { HttpCore } from '../http';

/**
 * `POST /tutor/stream` — SSE over POST, api-proxy (port 3004), consumed via
 * `fetch` + `ReadableStream` on web
 * (`frontend/web-ide/src/hooks/useTutorStream.ts`). Deliberately NOT wired
 * to `HttpCore` here: `fetch`+`ReadableStream` streaming is a separate
 * mobile-only transport task (mobile-app-plan M4.12 — incremental XHR
 * parsing of `text/event-stream` on React Native, since RN's `fetch` doesn't
 * expose `response.body.getReader()`). This module only types the request
 * body and the two SSE frame shapes the stream emits, so both the existing
 * web transport and the future native transport code against the same
 * contract.
 */

export type TutorRole = 'user' | 'assistant';

export interface TutorWireMessage {
  role: TutorRole;
  content: string;
}

export interface TutorContext {
  cert_title?: string;
  course_title?: string;
  lesson_title?: string;
  /** Reading-surface grounding — present only when the tutor is answering beside a textbook chapter. */
  course_id?: string;
  chapter_id?: string;
}

export interface TutorStreamRequest {
  messages: TutorWireMessage[];
  context?: TutorContext;
}

/** One parsed `data: {...}` SSE frame from `/tutor/stream`. */
export type TutorStreamFrame = { content: string } | { done: true } | { error: string };

/** A citation the assistant turn was grounded on. url is a same-origin relative path or https. */
export interface TutorSource {
  id: string;
  title: string;
  url?: string;
  kind?: 'lesson' | 'video' | 'reading' | 'lab' | 'external';
}

/** Final-event metadata of an assistant turn (teaching-agent `end` event data). */
export interface TutorMessageMeta {
  /** Server-generated stable message id (UUID); the key for feedback. */
  messageId: string;
  /** Present only when real grounding was used. */
  sources?: TutorSource[];
}

export type TutorFeedbackRating = 'up' | 'down';
export type TutorFeedbackReason =
  | 'wrong'
  | 'unclear'
  | 'off_topic'
  | 'too_easy'
  | 'too_hard'
  | 'unsafe'
  | 'other';

export interface TutorFeedbackPayload {
  rating: TutorFeedbackRating;
  reasons?: TutorFeedbackReason[];
  /** Free text, max 1000 chars (server strips control characters). */
  comment?: string;
  sessionId?: string;
}

export interface TutorFeedbackResult {
  id: string;
  rating: TutorFeedbackRating;
}

export const TUTOR_STREAM_PATH = '/tutor/stream';

/** No HttpCore dependency — see file-level note. Exposes the path constant
 *  for symmetry with the other modules' `createXModule(core)` shape. */
export function createTutorModule(core?: HttpCore) {
  return {
    streamPath: TUTOR_STREAM_PATH,
    /** `POST /portal/tutor/messages/:messageId/feedback` — idempotent per (message, user). */
    sendTutorFeedback: (messageId: string, payload: TutorFeedbackPayload): Promise<TutorFeedbackResult> => {
      if (!core) throw new Error('createTutorModule: HttpCore required for sendTutorFeedback');
      return core.request<TutorFeedbackResult>(
        `/portal/tutor/messages/${encodeURIComponent(messageId)}/feedback`,
        { method: 'POST', body: payload },
      );
    },
  };
}

export type TutorModule = ReturnType<typeof createTutorModule>;
